import { ProductDisplayStep } from '@digilize/shared/definitions/src';

export type ProductAvailabilityKind = 'stock' | 'seats' | 'infinite';

export interface ProductAvailability {
  kind: ProductAvailabilityKind;
  available: number | null;
  total: number | null;
  soldOut: boolean;
  almostSoldOut: boolean;
  showId: string | null;
}

/**
 * What the shop may still do with a product, given what the buyer already put in the cart
 * (PAUL-3153). `sold-out` replaces the quantity counter with a label — there is nothing to count
 * and nothing to give back; `limit-reached` keeps the counter so the lines already taken can be
 * removed, and only stops the + button.
 */
export type TicketAvailabilityState = 'available' | 'limit-reached' | 'sold-out';

/**
 * Availability is read once with the product list and never refetched, so what is left is the
 * `available` snapshot minus the lines this cart has since taken. A product with no availability at
 * all, or an infinite one, is never capped.
 */
export function ticketAvailabilityState(product: Product, quantity: number): TicketAvailabilityState {
  const availability = product.availability;
  if (!availability || availability.kind === 'infinite') {
    return 'available';
  }
  if (availability.soldOut) {
    return quantity > 0 ? 'limit-reached' : 'sold-out';
  }
  return availability.available !== null && quantity >= availability.available ? 'limit-reached' : 'available';
}

/**
 * One amount a product is sold at instead of at its own price — the dashboard's "most frequent
 * values" on the price step. Only gift cards use them online (PAUL-3071): such a product carries a
 * `0.00` price row that exists purely to hold the VAT rate, and the picked amount is sent as the
 * reservation's `custom_value`.
 */
export interface ProductPredefinedAmount {
  /** The `product_frequent_values` row id — a stable trackBy key only; never sent to the backend. */
  id: string;
  value: number;
}

/**
 * Predefined amounts arrive as raw decimals with 27 fractional digits and are matched against the
 * cart's own line amounts, so both sides are read at the cent precision money actually has.
 */
export function roundToCents(value: number): number {
  return Math.round(value * 100) / 100;
}

export interface Product {
  id: string;
  name: string;
  price: number;
  priceBefore: number;
  currency: string;
  image?: string;
  type: ProductType;
  seatTypes: string[];
  order: number;
  group: ProductGroup;
  mainProductId?: string;
  isLimitedToMainProduct?: boolean;
  isMutuallyExclusive?: boolean;
  displayStep?: ProductDisplayStep;
  hasReservation: boolean;
  companyId: string;
  description: string | null;
  supplement_ids: string[];
  required_payment_method_id: string;
  is_cineville_ticket: boolean;
  /** Ticket admitted against a subscription card (PAUL-2844) — the trigger for the card prompt. */
  is_subscription_ticket: boolean;
  availability: ProductAvailability | null;
  /**
   * The amounts this product is sold at, in the order the dashboard put them. Null unless the
   * product actually offers them, so a product with amounts is told from one priced normally by this
   * field alone — `price` is meaningless on the former.
   */
  predefinedAmounts: ProductPredefinedAmount[] | null;
}

/**
 * The product group a product is filed under in the dashboard. Only the primary group is kept —
 * the first entry of the row's `groups`, which is also the one the product's own `order` counts
 * its position in.
 */
export interface ProductGroup {
  id: string;
  title: string;
  order: number;
}

export interface Supplement extends Product {
  mainProductId?: string;
  isLimitedToMainProduct?: boolean;
  isMutuallyExclusive?: boolean;
  displayStep?: ProductDisplayStep;
}

export enum ProductType {
  RestaurantMeal = 2, // service in dashboard
  TicketService = 6, // ticket in dashboard
  Meal = 9, // reservation in dashboard
  // 12 (PrepaidTicket) is retired (PAUL-2844): a subscription ticket is now an ordinary ticket (6)
  // flagged `is_subscription_ticket` — key card behaviour off the flag, never off the type.
}
